import React from "react";
import { Link, useHistory } from "react-router-dom";
import { styled } from "@mui/material";

import { IActivityLogsData } from "../../../../../api/agentActivityLog/index.types";
import { getTimeByAmericaChicagoTz, getTypeTime } from "../../../../../utils/moment";
import { getCellValue } from "../../../../../pages/Discounts/components/ChangeLogs/TableBody/TableBody";
import { StyledTdWithHover } from "../../../../../useGeneralStyles";
import { IntlFormatUSD } from "../../../../../utils";
import { CENTS_IN_DOLLAR } from "../../../../../constants";
import { useAdminProviderDataContext, useDiscountsContext } from "../../../../../context";
import { getStringLogs, getMoneyValue } from "./utils";

const MONEY_ACTIONS = ["change_billing_amount"];

const Td = styled("td")({ whiteSpace: "break-spaces" });
interface IProps {
  logs: IActivityLogsData[];
  tableColumns: string[];
}
const TableBody: React.FC<IProps> = ({ logs, tableColumns }) => {
  const history = useHistory();

  const { setSearch: setDiscountCodesSearch, search: discountCodesSearch } = useDiscountsContext();
  const { user } = useAdminProviderDataContext();

  const handleDiscountCodeClick = (code: string): void => {
    setDiscountCodesSearch({
      ...discountCodesSearch,
      code,
    });
    history.push("/discounts");
  };

  const isShowDateColumn = tableColumns.includes("log_time");
  const isShowAgentColumn = tableColumns.includes("agent");
  const isShowActionColumn = tableColumns.includes("action");
  const isShowPreviousValueColumn = tableColumns.includes("previous_value");
  const isShowNewValueColumn = tableColumns.includes("new_value");
  const isShowCustomerColumn = tableColumns.includes("customer");
  const isShowUpdatedAgentColumn = tableColumns.includes("updated_agent") && user.is_manager;
  const isShowCodeColumn = tableColumns.includes("code");

  const getChangedValuesData = (log: IActivityLogsData): React.ReactElement | null => {
    const {
      log_admin_logins_id,
      discount_code_log_changes_id,
      discount_code_log_change,
      log_admin_accounts_activity_id,
      log_admin_accounts_activity,
      admin_accounts_log_profile_activity_id,
      admin_accounts_log_profile_activity,
    } = log;

    if (admin_accounts_log_profile_activity_id && admin_accounts_log_profile_activity) {
      const { field_name, previous_field_value, new_field_value, admin_account } =
        admin_accounts_log_profile_activity;
      return (
        <>
          {isShowActionColumn && <td data-test="action-name">{field_name}</td>}
          {isShowPreviousValueColumn && (
            <Td data-test="previous-field-value">
              {previous_field_value && getStringLogs(previous_field_value)}
            </Td>
          )}
          {isShowNewValueColumn && (
            <Td data-test="new-field-value">{new_field_value && getStringLogs(new_field_value)}</Td>
          )}
          {isShowCustomerColumn && <td />}
          {isShowUpdatedAgentColumn && (
            <td>
              <Link to={`/agent/${admin_account.id}`}>{admin_account.name}</Link>
            </td>
          )}
          {isShowCodeColumn && <td />}
        </>
      );
    }
    if (log_admin_logins_id) {
      return (
        <>
          {isShowActionColumn && <td data-test="action-name">login</td>}
          {isShowPreviousValueColumn && <td />}
          {isShowNewValueColumn && <td />}
          {isShowCustomerColumn && <td />}
          {isShowCodeColumn && <td />}
          {isShowUpdatedAgentColumn && <td />}
        </>
      );
    }

    if (discount_code_log_changes_id && discount_code_log_change) {
      const { field_name, previous_field_value, new_field_value, discount_code } =
        discount_code_log_change;

      return (
        <>
          {isShowActionColumn && (
            <td data-test="action-name">{discount_code_log_change?.field_name}</td>
          )}
          {isShowPreviousValueColumn && (
            <Td data-test="previous-field-value">
              {getCellValue(field_name, previous_field_value)}
            </Td>
          )}
          {isShowNewValueColumn && (
            <Td data-test="new-field-value">{getCellValue(field_name, new_field_value)}</Td>
          )}
          {isShowCustomerColumn && (
            <td>
              <Link to={`/customer/${discount_code_log_change?.accounts_id}`}>
                {discount_code_log_change?.account?.name || ""}
              </Link>
            </td>
          )}

          {isShowUpdatedAgentColumn && <td />}
          {isShowCodeColumn && (
            <StyledTdWithHover
              data-test="discount-code"
              onClick={() => handleDiscountCodeClick(discount_code?.code)}
            >
              {discount_code?.code}
            </StyledTdWithHover>
          )}
        </>
      );
    }

    if (log_admin_accounts_activity_id) {
      let prevTableDataValue = "";
      let newTableDataValue = "";
      const previous_field_value = log_admin_accounts_activity?.previous_field_value;
      const new_field_value = log_admin_accounts_activity?.new_field_value;
      if (previous_field_value) {
        prevTableDataValue = getStringLogs(previous_field_value);
      }
      if (new_field_value) {
        newTableDataValue = getStringLogs(new_field_value);
      }

      const field_name = log_admin_accounts_activity?.field_name || "";
      const isMoneyAction = MONEY_ACTIONS.includes(field_name);

      return (
        <>
          {isShowActionColumn && (
            <td data-test="action-name">{log_admin_accounts_activity?.field_name}</td>
          )}
          {isShowPreviousValueColumn && (
            <Td data-test="previous-field-value">
              {isMoneyAction
                ? IntlFormatUSD(
                    getMoneyValue(log_admin_accounts_activity?.previous_field_value as string) /
                      CENTS_IN_DOLLAR
                  )
                : prevTableDataValue}
            </Td>
          )}
          {isShowNewValueColumn && (
            <Td data-test="new-field-value">
              {isMoneyAction
                ? IntlFormatUSD(
                    getMoneyValue(log_admin_accounts_activity?.new_field_value as string) /
                      CENTS_IN_DOLLAR
                  )
                : newTableDataValue}
            </Td>
          )}
          {isShowCustomerColumn && (
            <td data-test="customer-link">
              <Link to={`/customer/${log_admin_accounts_activity?.accounts_id}`}>
                {log_admin_accounts_activity?.account?.name}
              </Link>
            </td>
          )}

          {isShowUpdatedAgentColumn && <td />}
          <td />
        </>
      );
    }
    return null;
  };

  return (
    <tbody>
      {logs.map((log) => (
        <tr key={log.id} data-test="log-data">
          {isShowDateColumn && (
            <td data-test="log-time">
              {getTimeByAmericaChicagoTz(log.log_time, true)} {getTypeTime(log.log_time)}
            </td>
          )}
          {isShowAgentColumn && (
            <td>
              <Link data-test="agent" to={`agent/${log.admin_accounts_id}`}>
                {log.admin_account.name}
              </Link>
            </td>
          )}

          {getChangedValuesData(log)}
        </tr>
      ))}
    </tbody>
  );
};

export default TableBody;
